import { useEffect, useState } from "react";
import { Compass, Puzzle, Sparkles, BookOpen, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AssetPlayer from "@/components/assets/AssetPlayer";
import ExportMenu from "@/components/assets/ExportMenu";

// Bounded sections — no feed. Families map to the four learner-facing moods.
const SECTIONS = [
  { key: "secTry", icon: Sparkles, fams: ["memory", "form", "worksheet"] },
  { key: "secPattern", icon: Compass, fams: ["grammar"] },
  { key: "secSolve", icon: Puzzle, fams: ["game", "data"] },
  { key: "secRead", icon: BookOpen, fams: ["reading", "document"] },
];
const SEEN = "fl_lab_seen";

export default function DiscoveryLab() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [progress, setProgress] = useState({});
  const [more, setMore] = useState({});
  const loadProgress = () => api.get("/lab/progress").then((r) => setProgress(r.data.progress)).catch(() => {});
  useEffect(() => {
    api.get("/lab/assets").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    localStorage.removeItem(SEEN); // progress now lives on the server for signed-in learners
    loadProgress();
  }, []);
  const show = async (id) => {
    try {
      setOpen((await api.get(`/lab/assets/${id}`)).data);
      if (!progress[id]) api.post(`/lab/assets/${id}/progress`, { status: "started" }).then(loadProgress).catch(() => {});
    } catch { toast.error(t("al.failed")); }
  };
  const check = async (answers) => { const r = (await api.post(`/lab/assets/${open.asset_id}/check`, { language: open.language, answers })).data; loadProgress(); return r; };
  const flashDone = () => api.post(`/lab/assets/${open.asset_id}/progress`, { status: "completed" }).then(loadProgress).catch(() => {});
  const keep = async () => {
    try { await api.post(`/lab/assets/${open.asset_id}/keep?lang=${open.language}`, {}); return true; }
    catch { toast.error(t("al.failed")); return false; }
  };
  return (
    <AppLayout>
      <Seo title="Discovery Lab" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="discovery-lab">
        <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl">{t("al.lab")}</h1>
        <p className="text-gray-700 mt-2">{t("al.labSub")}</p>
        <p className="text-xs text-gray-500 mt-1" data-testid="lab-preview-note">{t("al.previewNote")}</p>
        {SECTIONS.map(({ key, icon: Icon, fams }) => {
          const all = (items || []).filter((a) => fams.includes(a.family));
          const rows = more[key] ? all : all.slice(0, 4);
          if (!rows.length) return null;
          return (
            <section key={key} className="mt-8" data-testid={`lab-section-${key}`}>
              <h2 className="font-cabinet font-bold text-lg flex items-center gap-2"><Icon className="w-5 h-5" /> {t(`al.${key}`)}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                {rows.map((a) => (
                  <button key={a.asset_id} onClick={() => show(a.asset_id)} className="nb-card bg-white p-4 text-start hover:-translate-y-0.5 transition-transform" data-testid={`lab-card-${a.asset_id}`}>
                    <div className="flex gap-1.5 text-[11px]"><span className="nb-tag bg-mint">{a.cefr}</span><span className="nb-tag bg-sky">{t(`al.fam.${a.family}`)}</span></div>
                    <div className="font-bold mt-2" dir="auto">{a.title}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">{t("al.minutes", { n: a.duration_min })} · <span dir="ltr">{a.target_languages.join(" · ").toUpperCase()}</span>
                      {progress[a.asset_id] && <span className="ms-auto flex items-center gap-0.5" data-testid={`lab-seen-${a.asset_id}`}><Check className="w-3 h-3" /> {progress[a.asset_id].status === "completed" ? t("al.completed") : t("al.explored")}</span>}</div>
                  </button>
                ))}
              </div>
              {all.length > 4 && <button onClick={() => setMore({ ...more, [key]: !more[key] })} className="text-sm underline mt-2" data-testid={`lab-more-${key}`}>{more[key] ? t("al.p.showLess") : t("al.p.showMore", { n: all.length - 4 })}</button>}
            </section>
          );
        })}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0 w-[calc(100vw-1rem)] sm:w-full" data-testid="lab-activity">
          {open && (
            <>
              <DialogHeader className="text-start sm:text-start">
                <DialogTitle className="font-cabinet text-2xl pe-8" dir="auto" data-testid="lab-activity-title">{open.title}</DialogTitle>
                <p className="text-sm text-gray-600" dir="auto">{open.student_instructions}</p>
              </DialogHeader>
              <AssetPlayer family={open.family} language={open.language} content={open.content} onCheck={open.content.items || open.content.crossword ? check : null}
                keep={open.keep} onKeep={keep} onFlashDone={flashDone} />
              <div className="border-t-2 border-ink/10 pt-3"><ExportMenu base={`/lab/assets/${open.asset_id}/export`} lang={open.language} name={open.asset_id} /></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
